import { ReactElement } from 'react';

export type ChipOption<T extends string | number> = {
    value: T;
    label: string;
    disabled?: boolean;
};

export type ChipGroupProps<T extends string | number> = {
    options: ChipOption<T>[];
    selectedValue?: T;
    onChange: (value: T) => void;
    ariaLabel: string;
    role?: 'tablist' | 'listbox';
    itemRole?: 'tab' | 'option';
    className?: string;
};

export const ChipGroup = <T extends string | number>({
    options,
    selectedValue,
    onChange,
    ariaLabel,
    role = 'tablist',
    itemRole = 'tab',
    className = '',
}: ChipGroupProps<T>): ReactElement => (
    <div className={`flex flex-wrap gap-2 ${className}`} role={role} aria-label={ariaLabel}>
        {options.map((option) => {
            const isSelected = option.value === selectedValue;

            return (
                <button
                    type="button"
                    role={itemRole}
                    aria-selected={isSelected}
                    disabled={option.disabled}
                    key={option.value}
                    onClick={() => onChange(option.value)}
                    className={`tx-medium-regular rounded-full border px-2.5 py-1 transition-colors focus:outline-none focus:ring-2 focus:ring-green-light lg:px-3 cursor-pointer ${
                        isSelected
                            ? 'border-transparent bg-gradient-dark text-white-normal'
                            : 'border-gray-3 bg-transparent text-gray-2 hover:border-transparent hover:bg-gradient-dark hover:text-white-normal dark:border-gray-3 dark:text-gray-3'
                    } ${
                        option.disabled
                            ? 'cursor-not-allowed opacity-50 hover:border-gray-3 hover:bg-transparent hover:text-gray-2'
                            : ''
                    }`}
                >
                    {option.label}
                </button>
            );
        })}
    </div>
);

export default ChipGroup;
